import type { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, handleError } from '../../lib/errors.js';
import {
  providerLinks,
  providerLink,
  renderPlainCard,
  echoDirectiveToStderr,
} from '../../lib/ui-links.js';
import { validateProviderType } from './index.js';

/** Scope option type */
type ScopeOption = 'workspace' | 'org' | 'user';

interface AddOptions {
  provider: string;
  name: string;
  apiKey: string;
  scope?: ScopeOption;
  baseUrl?: string;
  setDefault?: boolean;
}

/**
 * Resolve scope IDs from the config based on the --scope flag.
 * - workspace (default): uses loadConfig().defaultWorkspace
 * - org: uses loadConfig().defaultOrganization
 * - user: personal config (no scope IDs needed, server infers from auth)
 */
function resolveScope(
  scopeFlag: ScopeOption | undefined,
  client: Awaited<ReturnType<typeof getSDKClient>>,
): { workspaceId?: string; organizationId?: string; userId?: string } {
  const scope = scopeFlag ?? 'workspace';

  if (scope === 'workspace') {
    const wsId = client.getCurrentWorkspaceId();
    if (!wsId) {
      throw new MutagentError(
        'MISSING_WORKSPACE',
        'No workspace configured. Cannot create workspace-scoped provider.',
        'Run: mutagent config set defaultWorkspace <workspace-id>'
      );
    }
    return { workspaceId: wsId };
  }

  if (scope === 'org') {
    const orgId = client.getCurrentOrgId();
    if (!orgId) {
      throw new MutagentError(
        'MISSING_ORGANIZATION',
        'No organization configured. Cannot create org-scoped provider.',
        'Run: mutagent config set defaultOrganization <org-id>'
      );
    }
    return { organizationId: orgId };
  }

  // scope === 'user': server infers userId from the authenticated API key
  return {};
}

export function registerAddCommand(parent: Command): void {
  parent
    .command('add')
    .description('Add a new provider configuration')
    .requiredOption('-p, --provider <type>', 'Provider type (openai, anthropic, google, ...)')
    .requiredOption('-n, --name <name>', 'Display name for this provider')
    .requiredOption('-k, --api-key <key>', 'API key for the provider')
    .option('-s, --scope <scope>', 'Scope: workspace (default), org, user', 'workspace')
    .option('--base-url <url>', 'Custom base URL for the provider API')
    .option('--set-default', 'Set as default provider for this scope')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent providers add --provider openai --name "My OpenAI" --api-key $OPENAI_API_KEY
  ${chalk.dim('$')} mutagent providers add --provider anthropic --name "Team Claude" --api-key $KEY --scope org
  ${chalk.dim('$')} mutagent providers add --provider openai --name "Personal" --api-key $KEY --scope user --set-default
  ${chalk.dim('$')} mutagent providers add --provider custom --name "Ollama" --api-key none --base-url http://localhost:11434 --json

Scope Resolution:
  workspace (default)   Uses your configured workspace
  org                   Uses your configured organization
  user                  Personal provider config

The API key is encrypted server-side and never returned in plain text.
    `)
    .action(async (options: AddOptions) => {
      const isJson = getJsonFlag(parent);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // Validate provider type
        const providerType = validateProviderType(options.provider);

        // Validate API key is not empty
        if (!options.apiKey || options.apiKey.trim() === '') {
          throw new MutagentError(
            'MISSING_API_KEY',
            'API key cannot be empty',
            'Provide a valid API key with --api-key'
          );
        }

        // Validate scope option
        const validScopes: ScopeOption[] = ['workspace', 'org', 'user'];
        if (options.scope && !validScopes.includes(options.scope)) {
          throw new MutagentError(
            'INVALID_SCOPE',
            `Invalid scope: ${options.scope}`,
            `Valid scopes: ${validScopes.join(', ')}`
          );
        }

        const client = await getSDKClient();
        const scope = resolveScope(options.scope, client);

        const created = await client.createProvider({
          name: options.name,
          provider: providerType,
          apiKey: options.apiKey,
          scope,
          baseUrl: options.baseUrl,
          isDefault: options.setDefault,
        });

        if (isJson) {
          const directive = buildProviderCreatedDirective(created, options.scope ?? 'workspace');
          echoDirectiveToStderr(directive);
          output.output({
            success: true,
            ...created,
            _links: providerLinks(created.id),
            _directive: directive,
          });
        } else {
          output.success(`Provider created: ${created.name ?? options.name}`);
          console.log(`  ID:    ${String(created.id)}`);
          console.log(`  Type:  ${providerType}`);
          console.log(`  Scope: ${options.scope ?? 'workspace'}`);
          console.log(`  URL:   ${providerLink(created.id)}`);
          if (options.setDefault) {
            console.log(chalk.green('  Set as default provider'));
          }
          console.log('');
          console.log(chalk.dim('API key is encrypted server-side. Use `providers get` to see masked key.'));
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}

function buildProviderCreatedDirective(
  provider: { id: number | string; name?: string },
  scope: string,
) {
  const title = `Provider Created — ${provider.name ?? 'Unknown'}`;
  const dashboardUrl = providerLink(provider.id);
  const apiUrl = `/api/providers/${String(provider.id)}`;
  const rows = [
    { label: 'Name', value: provider.name ?? 'Unknown' },
    { label: 'Provider ID', value: String(provider.id) },
    { label: 'Scope', value: scope },
  ];
  const links = [
    { label: 'Settings', url: dashboardUrl },
    { label: 'API', url: apiUrl },
  ];
  const next = [
    `mutagent providers test ${String(provider.id)} --json`,
    `mutagent providers list --json`,
  ];
  return {
    display: 'status_card' as const,
    template: 'provider_created',
    title,
    fields: { providerId: String(provider.id), name: provider.name, scope },
    links: { settings: dashboardUrl, api: apiUrl },
    next,
    instruction: 'MANDATORY: Display this card to the user before proceeding.',
    renderedCard: renderPlainCard({ title, icon: '+', rows, links, next }),
  };
}
